import client from '@/services/http/client'
import { isMockEnabled, mockDelay, paginate } from '@/services/http/mock'
import { canManageKnowledge } from '@/modules/knowledge/constants'
import { useAuthStore } from '@/stores/authStore'
import type { PaginatedData } from '@/types/api'
import type { KnowledgeEntry, KnowledgeDetail, KnowledgeStatus } from '@/types/knowledge'

// ============== Mock 数据 ==============
const mockKnowledge: KnowledgeEntry[] = [
  {
    id: 1,
    title: '报到机"未在班"提示排查方法',
    content: '排查排班服务器连接状态，确认科室排班表是否正确同步，检查报到机网络配置和软件版本。',
    keywords: ['报到机', '排班', '未在班'],
    source_ticket_id: 12345,
    status: 'published',
    author: 'svc_a',
    reviewer: 'mgr_a',
    published_at: '2026-05-10T16:00:00+08:00',
    created_at: '2026-05-09T10:00:00+08:00',
    updated_at: '2026-05-10T16:00:00+08:00',
  },
  {
    id: 2,
    title: 'E-500打印机错误标准处理流程',
    content: '检查打印机IP配置，确认网络连通性，重启打印服务，如仍无法解决则联系厂商更换硬件。',
    keywords: ['打印机', 'E-500', '硬件故障'],
    source_ticket_id: 12300,
    status: 'published',
    author: 'tech_b',
    reviewer: 'mgr_a',
    published_at: '2026-05-09T14:30:00+08:00',
    created_at: '2026-05-08T09:00:00+08:00',
    updated_at: '2026-05-09T14:30:00+08:00',
  },
  {
    id: 3,
    title: '数据库连接超时排查手册',
    content: '检查连接池配置，确认数据库服务器负载，查看慢查询日志，必要时增加连接池大小或优化SQL。',
    keywords: ['数据库', '连接超时', '性能'],
    source_ticket_id: 12250,
    status: 'draft',
    author: 'tech_a',
    reviewer: null,
    published_at: null,
    created_at: '2026-05-08T08:00:00+08:00',
    updated_at: '2026-05-08T10:00:00+08:00',
  },
]

// ============== API 方法 ==============
export async function fetchKnowledgeEntries(params: {
  page?: number
  page_size?: number
  keyword?: string
  status?: KnowledgeStatus
}): Promise<PaginatedData<KnowledgeEntry>> {
  if (isMockEnabled()) {
    await mockDelay()
    const { roles } = useAuthStore.getState()
    const requestedStatus = params.status ?? 'published'
    if (requestedStatus !== 'published' && !canManageKnowledge(roles)) {
      throw Object.assign(new Error('仅 manager/admin 可浏览未发布知识条目'), { code: 'FORBIDDEN' })
    }

    let filtered = mockKnowledge.filter((k) => k.status === requestedStatus)
    if (params.keyword) {
      const kw = params.keyword.toLowerCase()
      filtered = filtered.filter(
        (k) =>
          k.title.toLowerCase().includes(kw) ||
          k.content.toLowerCase().includes(kw)
      )
    }
    return paginate(filtered, params.page || 1, params.page_size || 20)
  }
  const { data } = await client.get<PaginatedData<KnowledgeEntry>>('/knowledge', { params })
  return data
}

export async function fetchKnowledgeDetail(entryId: number): Promise<KnowledgeDetail> {
  if (isMockEnabled()) {
    await mockDelay()
    const detail = mockKnowledge.find((k) => k.id === entryId)
    if (!detail) throw Object.assign(new Error('知识条目不存在'), { code: 'NOT_FOUND' })
    const { roles } = useAuthStore.getState()
    if (detail.status !== 'published' && !canManageKnowledge(roles)) {
      throw Object.assign(new Error('知识条目不存在'), { code: 'NOT_FOUND' })
    }
    return { ...detail }
  }
  const { data } = await client.get<KnowledgeDetail>(`/knowledge/${entryId}`)
  return data
}

export async function publishKnowledge(entryId: number, reviewer?: string): Promise<KnowledgeDetail> {
  if (isMockEnabled()) {
    await mockDelay()
    const { roles, actor } = useAuthStore.getState()
    if (!canManageKnowledge(roles)) {
      throw Object.assign(new Error('当前角色无发布权限'), { code: 'FORBIDDEN' })
    }
    const entry = mockKnowledge.find((k) => k.id === entryId)
    if (!entry) throw Object.assign(new Error('知识条目不存在'), { code: 'NOT_FOUND' })
    if (entry.status === 'published') {
      throw Object.assign(new Error('该知识条目已发布，不可重复发布'), { code: 'CONFLICT' })
    }
    entry.status = 'published'
    entry.published_at = new Date().toISOString()
    entry.reviewer = reviewer || actor || 'mgr_a'
    return { ...entry }
  }
  const { data } = await client.post<KnowledgeDetail>(`/knowledge/${entryId}/publish`, { reviewer })
  return data
}

export async function archiveKnowledge(entryId: number): Promise<KnowledgeDetail> {
  if (isMockEnabled()) {
    await mockDelay()
    const { roles } = useAuthStore.getState()
    if (!canManageKnowledge(roles)) {
      throw Object.assign(new Error('当前角色无归档权限'), { code: 'FORBIDDEN' })
    }
    const entry = mockKnowledge.find((k) => k.id === entryId)
    if (!entry) throw Object.assign(new Error('知识条目不存在'), { code: 'NOT_FOUND' })
    entry.status = 'archived'
    return { ...entry }
  }
  const { data } = await client.post<KnowledgeDetail>(`/knowledge/${entryId}/archive`)
  return data
}
